import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Store } from 'types/store';

import { GetDocument } from 'services/MyRacService';

// The insurance product information document is not yet returned by the renewal
// details endpoint, so this link points at the permanent Contentstack asset.
// MYR-20506 will source it dynamically from the CMS. The terms and conditions
// link is sourced from pageModel.documents.termsConditions (see Summary.jsx).
const IPID_DOCUMENT_URL = 'https://assets.contentstack.io/v3/assets/bltab270fecbe9695fb/bltb99f1e69f9c61bcc/IM_Combined_IPID.pdf';

interface RenewalDocument {
  DocumentId?: string;
  DocumentType?: string;
  Name?: string;
  documentLocation?: string;
}

interface ConfirmationProps {
  termsConditions?: RenewalDocument;
}

const Confirmation: React.FC<ConfirmationProps> = ({ termsConditions }) => {
  const customerId = useSelector((state: Store) => state.session.customerId);
  const { isAppView } = useSelector((state: Store) => state?.content?.flags);

  const dummyPath = ''; // Dummy filepath created to avoid linting errors
  const linkRef = useRef<HTMLAnchorElement>(null);

  const fetchTermsAndConditions = (e: React.MouseEvent) => {
    e.preventDefault();
    if (termsConditions) {
      // In-app, hand off PDF opening to the native layer instead of downloading
      if (isAppView) {
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'OPEN_PDF',
            documentId: termsConditions.DocumentId,
            policyId: 'booklet',
            name: termsConditions.Name,
          }));
        }
        return;
      }
      const documentType = termsConditions.DocumentType === 'Booklet' && termsConditions.DocumentType.toLowerCase();
      GetDocument(customerId, documentType, termsConditions.DocumentId)
        .then((blob: { payload: BlobPart }) => {
          const file = new Blob([blob.payload], {
            type: 'application/pdf',
          });
          // The linkRef controls the document download
          if (linkRef.current) {
            linkRef.current.href = URL.createObjectURL(file);
            linkRef.current.download = `${termsConditions.Name}.pdf`;
            linkRef.current.click();
          }
        });
    }
  };

  return (
    <div className="card border rounded my-4">
      <div className="card-body pb-0">
        <h5>Confirmation</h5>
        <p>It’s worth checking you don’t have equivalent cover elsewhere. For example, with your bank or car insurance.</p>
        <p>You can cancel your policy at any time. Whether a cancellation fee applies or a refund is due will depend on your policy type and circumstances. Please refer to your policy schedule for full details.</p>
        <p>
          By clicking ‘renew now’ you confirm that you’ve read the&nbsp;
          <a href={IPID_DOCUMENT_URL} target="_blank" rel="noopener noreferrer">insurance product information document</a>
          &nbsp;&amp; our&nbsp;
          {termsConditions?.documentLocation ? (
            <>
              <a onClick={fetchTermsAndConditions} href={dummyPath}>terms and conditions</a>
              <a className="d-none" role="button" href={dummyPath} ref={linkRef}>Download Ref</a>
            </>
          ) : (
            'terms and conditions'
          )}
          .
        </p>
      </div>
    </div>
  );
};

export default Confirmation;
